import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getServices } from '../utils/api';
import { decodeHTMLEntities } from '../utils/helpers';

export default function ServicesPage() {
    const [dynamicServices, setDynamicServices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadServices = async () => {
            const data = await getServices();
            if (data) {
                const mapped = data.map(item => {
                    const sm = item.service_meta || item.meta || {};
                    return {
                        id: item.id,
                        icon: sm.icon || sm.service_icon || '🏗️',
                        title: decodeHTMLEntities(item.title.rendered),
                        desc: decodeHTMLEntities(item.excerpt.rendered.replace(/<[^>]+>/g, '') || item.content.rendered.replace(/<[^>]+>/g, '').substring(0, 150) + '...'),
                        tags: sm.tags ? sm.tags.split(',') : (sm.service_tags ? sm.service_tags.split(',') : []),
                        size: sm.size || sm.service_size || 'small',
                        slug: item.slug
                    };
                });
                // Sort by ID or size to maintain consistency
                const sorted = [...mapped].sort((a, b) => (b.size === 'large' ? 1 : -1));
                setDynamicServices(sorted);
            }
            setLoading(false);
        };
        loadServices();
    }, []);

    if (loading) {
        return (
            <div className="pt-32 px-6 sm:px-8 flex items-center justify-center min-h-screen text-nocturne-muted">
                <div className="w-12 h-12 border-4 border-nocturne-purple border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    return (
        <div className="pt-16">
            {/* Header */}
            <section className="py-20 px-6 sm:px-8 text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
                <div className="relative max-w-4xl mx-auto">
                    <span className="section-tag border border-nocturne-purple/30 rounded-full px-4 py-1">CAPABILITIES</span>
                    <h1 className="text-5xl md:text-6xl font-black text-nocturne-text mt-6 mb-6">
                        The Digital <span className="gradient-text">Curator</span>
                    </h1>
                    <p className="text-nocturne-muted text-lg max-w-2xl mx-auto leading-relaxed">
                        We bridge the gap between technical precision and artistic vision. Our services are tailored to transform ambitious ideas into high-performance digital ecosystems.
                    </p>
                </div>
            </section>

            {/* Services grid */}
            <section className="py-12 px-6 sm:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                        {dynamicServices.slice(0, 2).map((s, i) => (
                            <Link key={s.id} to={`/service/${s.slug}`} className="card group flex flex-col">
                                <div className={`w-12 h-12 rounded-xl border flex items-center justify-center mb-6 transition-colors overflow-hidden ${i === 0 ? 'bg-nocturne-purple/10 border-nocturne-purple/30 text-nocturne-purple font-mono text-lg group-hover:bg-nocturne-purple/20' : 'bg-purple-900/30 border-purple-700/40 text-2xl'}`}>
                                    {s.icon}
                                </div>
                                <h2 className="text-2xl font-bold text-nocturne-text mb-3">{s.title}</h2>
                                <p className="text-nocturne-muted text-sm leading-relaxed mb-6">{s.desc}</p>

                                {s.tags && s.tags.length > 0 && (
                                    <div className="grid grid-cols-2 gap-2 mb-6">
                                        {s.tags.map((tag, i) => (
                                            <div key={i} className="flex items-center gap-2 text-nocturne-muted text-xs">
                                                <div className="w-1.5 h-1.5 rounded-full bg-nocturne-purple" />
                                                {tag}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <span className="mt-auto text-left text-nocturne-muted group-hover:text-nocturne-purple text-sm transition-colors">
                                    → View architecture details
                                </span>
                            </Link>
                        ))}
                    </div>

                    {/* Bottom row: remaining services */}
                    <div className="grid md:grid-cols-3 gap-6">
                        {dynamicServices.slice(2).map((s) => (
                            <Link key={s.id} to={`/service/${s.slug}`} className="card group cursor-pointer">
                                <div className="w-10 h-10 rounded-lg bg-nocturne-bg border border-nocturne-border flex items-center justify-center text-xl mb-4 group-hover:border-nocturne-purple/40 transition-colors overflow-hidden">
                                    {s.icon}
                                </div>
                                <h3 className="text-nocturne-text font-bold text-lg mb-3">{s.title}</h3>
                                <p className="text-nocturne-muted text-sm leading-relaxed">{s.desc}</p>
                                <span className="mt-4 block text-nocturne-muted group-hover:text-nocturne-purple text-sm transition-colors">→</span >
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 px-6 sm:px-8">
                <div className="max-w-3xl mx-auto card text-center py-16 border-nocturne-purple/30 bg-card-gradient">
                    <h2 className="text-4xl font-black text-nocturne-text mb-4">
                        Ready to build the <span className="gradient-text">Future?</span>
                    </h2>
                    <p className="text-nocturne-muted mb-8">Scale with the architects of the high-performance web. Let's start building.</p>
                    <div className="flex gap-4 justify-center flex-wrap">
                        <Link to="/contact" className="btn-primary px-8 py-3.5">
                            Schedule Consultation
                        </Link>
                        <Link to="/portfolio" className="btn-outline px-8 py-3.5">
                            View Case Studies
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
